import { appConfig, appConfig as config, Dropdown, getLoginData, GoogleMapsLink, Input, LocationSearchInput, NotesBanner } from '@ami/react/core';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { isAmiUserAuthorizedToManageDealerUsers, isAmiUserAuthorizedToManageQuoteGroups } from '../../../authorization/amiPermissions';
import { isUserDealerAdministrator } from '../../../authorization/permissions';
import { DealerBranch } from '../../../dealer-branches/dealerBranch';
import {
  DealerUserSearchResult,
  dealerUserService,
} from '../../../dealer-users/dealerUserService';
import { IndustriesAsyncDropdown } from '../../../industries/IndustriesAsyncDropdown';
import QuoteGroupAddModal from '../../../quote-groups/QuoteGroupAddModal';
import QuoteGroupsAsyncDropdown from '../../../quote-groups/QuoteGroupsAsyncDropdown';
import { Quote } from '../../../quotes/quote';
import QuoteWizardContext from '../../../quotes/QuoteWizardContext';
import DealerInvitationModal from './DealerInvitationModal';
import ReviewQuoteInformationComponentExistingUser from './ReviewQuoteInformationComponentExistingUser';
import ReviewQuoteInformationComponentNewUser from './ReviewQuoteInformationComponentNewUser';

const DataItem = ({ label, show = true, children }) => {
  if (!show) return null;
  return (
    <div className="row">
      <label className="col-sm-3 col-form-label">{label}</label>
      <div className="col-sm-9">
        <div>{children}</div>
      </div>
    </div>
  );
};

const DataItemReadonly = ({ label, show = true, children }) => {
  if (!show) return null;
  return (
    <div className="row">
      <label className="col-sm-3 col-form-label">{label}</label>
      <div className="col-sm-9 mt-2">
        <div>{children}</div>
      </div>
    </div>
  );
};

const ShipTo = ({
  quote,
  onShowCustomShippingLocation,
  showAmiOperations,
  canPerformAmiOperations,
  showCustomShippingLocation,
  onLocationChange,
}) => {
  return (
    <React.Fragment>
      <DataItem label="Ship To">
        <Dropdown
          items={[
            {
              id: 0,
              label: `Dealer Branch (${quote.step1.dealerBranchSummary.dealerBranchName})`,
            },
            { id: 1, label: 'Custom' },
          ]}
          selectedId={showCustomShippingLocation ? 1 : 0}
          onChange={(selectedId) =>
            onShowCustomShippingLocation(selectedId === 1 ? true : false)
          }
        />
      </DataItem>
      <DataItem show={showAmiOperations && showCustomShippingLocation}>
        <LocationSearchInput
          className="mb-0"
          placeholder="Custom shipping location"
          location={quote.customShippingLocation}
          onLocationChange={onLocationChange}
          readOnly={!canPerformAmiOperations}
          required
        />
      </DataItem>
    </React.Fragment>
  );
};

const DealerUserDetail = ({
  dealerUsers,
  dealerUserId,
  onChange,
  onShowNewDealerUserModal,
  onShowExistingDealerUserModal,
  onShowDealerInvitationModal,
  canManageUsers,
  dealerInvitationSent
}) => {
  const dealerUser = dealerUsers.find((x) => x.id === dealerUserId);
  const isRegistered = !!dealerUser && dealerUser.isRegistered;

  return (
    <DataItem label="Contact">
      <Fragment>
        <Dropdown
          items={dealerUsers}
          placeholder="Select a dealer contact..."
          selectedId={dealerUserId}
          onChange={onChange}
          required
          forceSearchable={true}
        />
        {!isRegistered && dealerUserId && appConfig.isManagementApp && (
          <div className="row row-cols-1">
            <div className="col">
              <div className="banner-container">
                <div className="card-body alert alert-warning py-1 mb-0">
                  {!dealerInvitationSent && (
                    <div className="card-text d-flex justify-content-between align-items-center">
                      <div>User is not registered for Blackbird</div>
                      <button type="button" className="btn btn-link p-0 m-0" onClick={onShowDealerInvitationModal}>send invite</button>
                    </div>
                  )}

                  {dealerInvitationSent && (
                    <div className="card-text">
                      <span>Invitation sent</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {canManageUsers && (
          <Fragment>
            <button
              type="button"
              className="btn btn-link ps-0 pt-2 pe-2 pb-2"
              onClick={onShowNewDealerUserModal}
            >
              add new user
            </button>
            <button
              type="button"
              className="btn btn-link p-2 ps-3"
              onClick={onShowExistingDealerUserModal}
            >
              authorize existing user
            </button>
          </Fragment>
        )}
      </Fragment>
    </DataItem>
  );
};

const DealerUserDetailReadonly = ({ dealerUsers, dealerUserId }) => {
  const dealerUser = dealerUsers.find((x) => x.id === dealerUserId);
  if (!dealerUser) return <DataItemReadonly label="Contact"></DataItemReadonly>;
  return (
    <DataItemReadonly label="Contact">{dealerUser.label}</DataItemReadonly>
  );
};

export default class ReviewQuoteInformationComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCustomShippingLocation: !!props.quote.customShippingLocation,
      showNewDealerUserModal: false,
      showExistingDealerUserModal: false,
      showQuoteGroupAddModal: false,
      showDealerInvitationModal: false,
      dealerInvitationSent: false
    };
  }
  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    dealerBranch: PropTypes.instanceOf(DealerBranch).isRequired,
    onUpdate: PropTypes.func.isRequired,
    dealerUsers: PropTypes.arrayOf(PropTypes.instanceOf(DealerUserSearchResult))
      .isRequired,
    onNewDealerUserAssigned: PropTypes.func.isRequired,
    amiUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
    isEditMode: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    const { quote, dealerBranch, onUpdate } = this.props;
    if (
      appConfig.isManagementApp &&
      !quote.salesPersonId &&
      !quote.territoryManagerId
    ) {
      const copy = this.props.quote.clone();
      copy.salesPersonId = dealerBranch.defaultAMISalesPersonId;
      copy.territoryManagerId = dealerBranch.defaultTerritoryManagerId;
      onUpdate(copy);
    }
  }

  componentDidUpdate() {
    if (
      !this.state.showCustomShippingLocation &&
      this.props.quote.customShippingLocation
    ) {
      this.setState({ showCustomShippingLocation: true });
    }
  }

  updateValue = (property, value) => {
    const copy = this.props.quote.clone();
    copy[property] = value;
    this.props.onUpdate(copy);
  };

  onUpdateQuoteGroup = (value) => {
    const copy = this.props.quote.clone();
    if (value != null) {
      copy.quoteGroupId = value.id;
      copy.quoteGroupName = value.label;
      copy.notificationSettings.notificationLevel = 'None';
    } else {
      copy.quoteGroupId = null;
      copy.quoteGroupName = '';
    }
    this.props.onUpdate(copy);
  };

  onUpdateIndustry = (value) => {
    const copy = this.props.quote.clone();
    if (value != null) {
      copy.industryId = value.id;
      copy.industryName = value.label;
    } else {
      copy.industryId = null;
      copy.industryName = '';
    }
    this.props.onUpdate(copy);
  };

  newQuoteGroup = () => {
    this.setState({ showQuoteGroupAddModal: true });
  };

  cancelQuoteGroup = () => {
    this.setState({ showQuoteGroupAddModal: false });
  };

  invitationSent = () => {
    this.setState({ showDealerInvitationModal: false, dealerInvitationSent: true });
  }

  render() {
    const {
      quote,
      dealerUsers,
      dealerBranch,
      onNewDealerUserAssigned,
      amiUsers,
    } = this.props;
    const {
      showCustomShippingLocation,
      showNewDealerUserModal,
      showExistingDealerUserModal,
      showQuoteGroupAddModal,
      showDealerInvitationModal,
      dealerInvitationSent
    } = this.state;
    const canManageUsers =
      isAmiUserAuthorizedToManageDealerUsers() || isUserDealerAdministrator();
    const loginData = getLoginData();
    if (!loginData) return null;

    const dealerUser = dealerUsers.find((x) => x.id === quote.dealerUserId);

    return (
      <QuoteWizardContext.Consumer>
        {({ showAmiOperations, canPerformAmiOperations }) => (
          <div className="card mb-4 box-shadow quote-info">
            <div className="card-body">
              <h6 className="card-title text-center">Quote Information</h6>
              {canPerformAmiOperations && (
                <DataItemReadonly label="Price Zone">
                  {quote.step1.priceZoneSummary.label}
                </DataItemReadonly>
              )}
              <DataItemReadonly label="Branch">
                {quote.step1.dealerBranchSummary.dealerBranchName}
              </DataItemReadonly>
              <DataItem label="Industry">
                <IndustriesAsyncDropdown
                  placeholder="Select an industry"
                  onChange={(industry) => this.onUpdateIndustry(industry)}
                  selected={
                    quote.industryId
                      ? {
                        id: quote.industryId,
                        label: quote.industryName,
                      }
                      : null
                  }
                  carrierId={quote.step1.carrierId}
                  weightCategoryId={quote.step1.weightCategoryId}
                />
              </DataItem>
              {config.isDealerApp && (
                <DataItemReadonly label="Ship To">
                  {quote.customShippingLocation && (
                    <GoogleMapsLink location={quote.customShippingLocation} />
                  )}
                  {!quote.customShippingLocation && (
                    <span>
                      {quote.step1.dealerBranchSummary.dealerBranchName}
                    </span>
                  )}
                </DataItemReadonly>
              )}
              {config.isManagementApp && (
                <ShipTo
                  quote={quote}
                  showAmiOperations={showAmiOperations}
                  canPerformAmiOperations={canPerformAmiOperations}
                  showCustomShippingLocation={showCustomShippingLocation}
                  onShowCustomShippingLocation={(
                    showCustomShippingLocation
                  ) => {
                    this.setState({ showCustomShippingLocation });
                    this.updateValue('customShippingLocation', null);
                  }}
                  onLocationChange={(location) =>
                    this.updateValue('customShippingLocation', location)
                  }
                />
              )}
              {canManageUsers && (
                <DealerUserDetail
                  dealerUsers={dealerUsers}
                  dealerUserId={quote.dealerUserId}
                  onChange={(dealerUserId) =>
                    this.updateValue('dealerUserId', dealerUserId)
                  }
                  onShowNewDealerUserModal={() =>
                    this.setState({ showNewDealerUserModal: true })
                  }
                  onShowExistingDealerUserModal={() =>
                    this.setState({ showExistingDealerUserModal: true })
                  }
                  onShowDealerInvitationModal={() => {
                    this.setState({ showDealerInvitationModal: true })
                  }}
                  canManageUsers={canManageUsers}
                  dealerInvitationSent={dealerInvitationSent}
                />
              )}
              {!canManageUsers && (
                <DealerUserDetailReadonly
                  dealerUsers={dealerUsers}
                  dealerUserId={quote.dealerUserId || loginData.userData.id}
                />
              )}
              {canPerformAmiOperations && dealerUser && (
                <DealerInvitationModal
                  dealerUserId={dealerUser?.userId ?? ''}
                  dealerUserName={dealerUser?.fullName ?? ''}
                  onChanged={this.invitationSent}
                  show={showDealerInvitationModal}
                  onHide={() => this.setState({ showDealerInvitationModal: false })} />
              )}
              {canPerformAmiOperations && (
                <Fragment>
                  <DataItem label="Sales Person">
                    <Dropdown
                      placeholder="Select a sales person..."
                      label="Sales Person"
                      items={amiUsers}
                      selectedId={quote.salesPersonId}
                      onChange={(salesPersonId) =>
                        this.updateValue('salesPersonId', salesPersonId)
                      }
                      required
                      forceSearchable={true}
                    />
                  </DataItem>
                  <DataItem label="Territory Manager">
                    <Dropdown
                      placeholder="Select a Territory Manager..."
                      label="Territory Manager"
                      items={amiUsers}
                      selectedId={quote.territoryManagerId}
                      onChange={(territoryManagerId) =>
                        this.updateValue(
                          'territoryManagerId',
                          territoryManagerId
                        )
                      }
                      required
                      forceSearchable={true}
                    />
                  </DataItem>
                </Fragment>
              )}
              {config.isDealerApp && (
                <Fragment>
                  <DataItemReadonly label="Sales Person">
                    {dealerBranch.defaultSalesPersonName}
                  </DataItemReadonly>
                  <DataItemReadonly label="Territory Manager">
                    {dealerBranch.defaultTerritoryManagerName}
                  </DataItemReadonly>
                </Fragment>
              )}
              <DataItem label="Quote Name">
                <Input
                  label="Name"
                  placeholder="Provide a name for this quote"
                  value={quote.name}
                  onChange={(e) => this.updateValue('name', e.target.value)}
                  required
                />
              </DataItem>
              <DataItem label="Billing Instructions">
              <Input
                label="Billing Instructions"
                type="textarea"
                textAreaAutoRows={true}
                placeholder="Enter billing instructions"
                value={quote.billingInstructions}
                onChange={(e) =>
                  this.updateValue('billingInstructions', e.target.value)
                }
              />
              </DataItem>
              <DataItem label="Shipping Instructions">
                <Input
                  label="Shipping Instructions"
                  type="textarea"
                  textAreaAutoRows={true}
                  placeholder="Enter shipping instructions"
                  value={quote.shippingInstructions}
                  onChange={(e) =>
                    this.updateValue('shippingInstructions', e.target.value)
                  }
                />
              </DataItem>        
              <DataItem label="Notes">
                <Input
                  label="Notes"
                  type="textarea"
                  textAreaAutoRows={true}
                  placeholder="Enter some notes"
                  value={quote.notes}
                  onChange={(e) => this.updateValue('notes', e.target.value)}
                />
                { quote.notes.trim() && (
                  <NotesBanner />
                )}
              </DataItem>          
              {canPerformAmiOperations && (
                <DataItem show={showAmiOperations} label="Private Notes">
                  <Input
                    label="Private Notes"
                    type="textarea"
                    textAreaAutoRows={true}
                    placeholder={
                      canPerformAmiOperations
                        ? 'Enter private notes (visible to AMI only)'
                        : null
                    }
                    onFocus={(e) => {
                      if (!canPerformAmiOperations) e.target.blur();
                    }}
                    value={quote.privateNotes}
                    onChange={(e) =>
                      this.updateValue('privateNotes', e.target.value)
                    }
                    readOnly={!canPerformAmiOperations}
                  />
                </DataItem>
              )}

              {canPerformAmiOperations && (
                <DataItem label="Quote Group">
                  <QuoteGroupsAsyncDropdown
                    placeholder={'None'}
                    dealerGroupId={dealerBranch.dealerGroupId}
                    onChange={(quoteGroup) =>
                      this.onUpdateQuoteGroup(quoteGroup)
                    }
                    selected={
                      quote.quoteGroupId
                        ? {
                          id: quote.quoteGroupId,
                          label: quote.quoteGroupName,
                        }
                        : null
                    }
                  />
                  {isAmiUserAuthorizedToManageQuoteGroups() && (
                    <button
                      type="button"
                      className="btn btn-link ps-0 pt-2 pe-2 pb-2"
                      onClick={this.newQuoteGroup}
                    >
                      add quote group
                    </button>
                  )}
                  {isAmiUserAuthorizedToManageQuoteGroups() && (
                    <QuoteGroupAddModal
                      showModal={showQuoteGroupAddModal}
                      hideModal={this.cancelQuoteGroup}
                      title={'Quote Group'}
                      dealerGroupId={dealerBranch.dealerGroupId}
                      onNewQuoteGroupCreated={(quoteGroup) => {
                        this.onUpdateQuoteGroup({
                          id: quoteGroup.id,
                          label: quoteGroup.name,
                        });
                      }}
                      dealerContactId={quote.dealerUserId}
                    />
                  )}
                </DataItem>
              )}
            </div>
            {canManageUsers && (
              <Fragment>
                <ReviewQuoteInformationComponentNewUser
                  showModal={showNewDealerUserModal}
                  onHideModal={() =>
                    this.setState({ showNewDealerUserModal: false })
                  }
                  onNewUserCreated={(dealerUser) => {
                    if (
                      dealerUserService.isUserAuthorizedForDealerBranch(
                        dealerBranch.dealerGroupId,
                        dealerBranch.id,
                        dealerUser.authorizedDealerGroups
                      )
                    ) {
                      this.updateValue('dealerUserId', dealerUser.id);
                      onNewDealerUserAssigned();
                    } else {
                      this.updateValue('dealerUserId', undefined);
                    }
                    this.setState({ showNewDealerUserModal: false });
                  }}
                  dealerGroupId={dealerBranch.dealerGroupId}
                />
                <ReviewQuoteInformationComponentExistingUser
                  showModal={showExistingDealerUserModal}
                  onHideModal={() =>
                    this.setState({ showExistingDealerUserModal: false })
                  }
                  dealerBranch={dealerBranch}
                  onExistingUserAuthorized={(dealerUser) => {
                    if (
                      dealerUserService.isUserAuthorizedForDealerBranch(
                        dealerBranch.dealerGroupId,
                        dealerBranch.id,
                        dealerUser.authorizedDealerGroups
                      )
                    ) {
                      this.updateValue('dealerUserId', dealerUser.id);
                      onNewDealerUserAssigned();
                    }
                    this.setState({ showExistingDealerUserModal: false });
                  }}
                />
              </Fragment>
            )}
          </div>
        )}
      </QuoteWizardContext.Consumer>
    );
  }
}
